import { useState, useEffect } from "react";
import { Spin } from "antd";
import { DesktopSidebar } from "./DesktopSidebar";
import { MobileSidebar } from "./MobileSidebar";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch, clearCredentials } from "../../store";
import { useResponsive } from "../../hooks/useResponsive";
import { Header } from "./Header";
import { Footer } from "./Footer";
import type { AdminLayoutProps } from "../../types";

function AdminLayoutContent({ children }: AdminLayoutProps) {
  const { sidebarCollapsed } = useAppSelector((state) => state.settings);
  const headerProps = useAppSelector((state) => state.ui.header);
  const { isMobile } = useResponsive();

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Close mobile drawer on desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [isMobile]);

  const toggleMobileDrawer = () => setMobileDrawerOpen(!mobileDrawerOpen);

  return (
    <div className="flex h-screen bg-white md:overflow-hidden">
      <DesktopSidebar collapsed={sidebarCollapsed} />
      <MobileSidebar
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      />
      <div className="flex flex-col flex-1 min-w-0 relative overflow-y-auto md:overflow-hidden">
        <Header onMobileMenuToggle={toggleMobileDrawer} {...headerProps} />

        <div className="absolute w-full bottom-15 pointer-events-none z-0 flex items-center justify-center">
          <img
            src="/service_x_black.svg"
            alt="Watermark"
            className="opacity-40 select-none"
          />
        </div>

        <main className="flex-1 relative px-2 py-4 z-10 md:overflow-y-auto">
          <div className="relative z-10 h-full">{children}</div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

export function Layout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Suspended staff guard — redirect to login with suspended flag
  useEffect(() => {
    if (isAuthenticated && user?.status === "SUSPENDED") {
      setIsRedirecting(true);
      dispatch(clearCredentials());
      navigate("/login", { replace: true, state: { suspended: true } });
    }
  }, [isAuthenticated, user?.status, dispatch, navigate]);

  // Show loading indicator during suspended user redirect
  if (!isAuthenticated || user?.status === "SUSPENDED") {
    if (isRedirecting) {
      return (
        <div className="flex justify-center items-center h-screen">
          <Spin size="large" />
        </div>
      );
    }
    return null;
  }

  return <AdminLayoutContent>{children}</AdminLayoutContent>;
}

export default Layout;
